import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import MakeItLiveCta from "../components/MakeItLiveCta.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/single-elimination-bracket-generator')({
    component: RouteComponent,
})

type Match = {team1: string; team2: string; bye1: boolean; bye2: boolean; placeholder: boolean};
type BracketRound = {title: string; matches: Match[]};

function nextPowerOfTwo(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)));
}

function roundName(size: number): string {
    if (size === 2) return "Final";
    if (size === 4) return "Semi-finals";
    if (size === 8) return "Quarter-finals";
    return `Round of ${size}`;
}

/** Build a single-elimination bracket: pad to a power of two with byes, seed round one, then placeholders. */
function buildBracket(entrants: string[]): BracketRound[] {
    const bracketSize = nextPowerOfTwo(entrants.length);
    const players = [...entrants];
    while (players.length < bracketSize) players.push("BYE");

    const totalRounds = Math.log2(bracketSize);
    const rounds: BracketRound[] = [];
    let currentRoundSize = bracketSize;

    for (let round = 0; round < totalRounds; round++) {
        const matches: Match[] = [];
        const matchesInRound = currentRoundSize / 2;
        for (let m = 0; m < matchesInRound; m++) {
            if (round === 0) {
                const p1 = players[m * 2];
                const p2 = players[m * 2 + 1];
                matches.push({team1: p1, team2: p2, bye1: p1 === "BYE", bye2: p2 === "BYE", placeholder: false});
            } else {
                matches.push({team1: "Winner of previous round", team2: "Winner of previous round", bye1: false, bye2: false, placeholder: true});
            }
        }
        rounds.push({title: roundName(currentRoundSize), matches});
        currentRoundSize /= 2;
    }

    return rounds;
}

const STEPS = [
    {title: "Add Participants", description: "Enter all players or teams taking part in your tournament."},
    {title: "Generate Bracket", description: "Create first-round knockout fixtures instantly."},
    {title: "Advance Winners", description: "Winners move forward until the final champion is crowned."},
];

const CALC_TABLE = [
    {n: 4, matches: 3, rounds: "Semi-final, Final"},
    {n: 8, matches: 7, rounds: "Quarter-final to Final"},
    {n: 16, matches: 15, rounds: "Round of 16 to Final"},
    {n: 32, matches: 31, rounds: "Round of 32 to Final"},
    {n: 64, matches: 63, rounds: "Round of 64 to Final"},
];

const BEST_FOR = ["Large tournaments", "One-day events", "Championship brackets", "Events with limited court time"];
const LESS_IDEAL = ["Social club nights", "Fair ranking events", "Leagues where everyone should play multiple matches", "Beginner-friendly competitions"];

const VS_ROWS = [
    {feature: "Fast to complete", se: "Yes", rr: "No"},
    {feature: "Every player gets multiple matches", se: "No", rr: "Yes"},
    {feature: "Best for large events", se: "Yes", rr: "No"},
    {feature: "Best for fair rankings", se: "No", rr: "Yes"},
    {feature: "Simple for spectators", se: "Yes", rr: "Yes"},
];

const FAQS = [
    {
        q: 'What is a single elimination bracket?',
        a: <>A single elimination bracket is a knockout structure where each match winner advances and each loser is eliminated, until one champion remains.</>,
    },
    {
        q: 'How many matches are in single elimination?',
        a: <>A single elimination tournament has n − 1 matches, where n is the number of participants. For example, 16 players produce 15 matches.</>,
    },
    {
        q: 'What happens if the number of players is not a power of two?',
        a: <>Some players receive byes into the next round so the bracket balances correctly. The generator adds the right number of byes automatically.</>,
    },
    {
        q: 'Can I use this for padel, tennis or pickleball?',
        a: <>Yes. This single elimination bracket generator works for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and other competitions.</>,
    },
    {
        q: 'Can ScorePal manage the full tournament?',
        a: <>Yes. ScorePal manages fixtures, live scores, standings, rankings and tournament communication from one platform.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [bracket, setBracket] = useState<BracketRound[] | null>(null);
    const [summary, setSummary] = useState<{participants: number; matches: number; rounds: number; byes: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Seeding the entrants", "Placing byes", "Drawing the bracket"]);

    // Same handoff the results panel uses — hoisted so the top CTA and MakeItLiveCta can't drift.
    const buildPayload = () => {
        const teams = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        return teams.length >= 2 ? {source: "single-elimination-bracket-generator", format: "knockout" as const, sport: "padel", teams} : null;
    };

    const generate = () => {
        const entrants = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (entrants.length < 2) {
            setError("Please enter at least two players or teams.");
            setBracket(null);
            setSummary(null);
            return;
        }
        const bracketSize = nextPowerOfTwo(entrants.length);
        setError(null);
        setBracket(null);
        setSummary(null);
        gen.run(() => {
            setBracket(buildBracket(entrants));
            setSummary({
                participants: entrants.length,
                matches: entrants.length - 1,
                rounds: Math.log2(bracketSize),
                byes: bracketSize - entrants.length,
            });
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Single Elimination Bracket Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a single elimination tournament bracket instantly. Generate knockout fixtures for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and more with ScorePal's free bracket generator." />
            <link rel="canonical" href={`${config.publicUrl}/single-elimination-bracket-generator/`} />
            <meta property="og:title" content="Single Elimination Bracket Generator | ScorePal" />
            <meta property="og:description" content="Generate a free single elimination bracket for any sport or competition." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/single-elimination-bracket-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/single-elimination-bracket-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Single Elimination Bracket Generator | ScorePal" />
            <meta name="twitter:description" content="Create knockout tournament brackets instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Single Elimination Bracket Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate single elimination tournament brackets for sports, clubs and competitions.",
                "url": "https://scorepal.co/single-elimination-bracket-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a single elimination bracket?", "acceptedAnswer": {"@type": "Answer", "text": "A single elimination bracket is a knockout structure where each match winner advances and each loser is eliminated."}},
                    {"@type": "Question", "name": "How many matches are in single elimination?", "acceptedAnswer": {"@type": "Answer", "text": "A single elimination tournament has n - 1 matches, where n is the number of participants."}},
                    {"@type": "Question", "name": "What happens if the number of players is not a power of two?", "acceptedAnswer": {"@type": "Answer", "text": "Some players receive byes into the next round so the bracket can balance correctly."}},
                    {"@type": "Question", "name": "Can I use this for padel, tennis or pickleball?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. This single elimination bracket generator works for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and other competitions."}},
                    {"@type": "Question", "name": "Can ScorePal manage the full tournament?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, live scores, standings, rankings and tournament communication."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Single Elimination Bracket Generator", item: "https://scorepal.co/single-elimination-bracket-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Bracket Generator
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Single Elimination Bracket Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create a knockout tournament bracket instantly. Add your players or teams, generate
                                    first-round fixtures and run single elimination tournaments for padel, tennis,
                                    pickleball, football, basketball, baseball, cricket, rugby, esports and more.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Bracket</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players or teams and generate a knockout bracket.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players or teams</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player or team per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate Single Elimination Bracket"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.participants}</strong> participants ·{" "}
                                        <strong>{summary.matches}</strong> matches ·{" "}
                                        <strong>{summary.rounds}</strong> rounds ·{" "}
                                        <strong>{summary.byes}</strong> byes
                                    </p>
                                )}

                                {bracket && <GeneratorTopCta buildPayload={buildPayload} />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results bracket */}
            {bracket && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                            Your Single Elimination Bracket
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 mb-8">Winners advance to the next round until one champion remains.</p>

                        <div className="flex gap-6 overflow-x-auto pb-4">
                            {bracket.map((round, ri) => (
                                <div key={ri} className="min-w-[240px] flex flex-col gap-4">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold">{round.title}</div>
                                    {round.matches.map((match, mi) => (
                                        <div key={mi} className="rounded-2xl border border-[#e5e7eb] overflow-hidden bg-white">
                                            {[{name: match.team1, bye: match.bye1}, {name: match.team2, bye: match.bye2}].map((t, ti) => (
                                                <div
                                                    key={ti}
                                                    className={`px-4 py-3 font-inter text-sm ${ti === 0 ? "border-b border-[#e5e7eb]" : ""} ${
                                                        match.placeholder ? "text-[#1a1a2e]/40 italic bg-[#FAFBFD]" : t.bye ? "text-[#1a1a2e]/40 italic bg-[#F8FAFC]" : "text-[#1a1a2e] bg-[#F8FAFC]"
                                                    }`}
                                                >
                                                    {t.name}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <MakeItLiveCta formatLabel="single-elimination" buildPayload={buildPayload} />
                    </div>
                </section>
            )}

            {/* What is single elimination / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Single Elimination Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A single elimination tournament — also known as a knockout tournament — is a format where a player
                        or team is eliminated after one loss. Winners continue to the next round until a champion is decided.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Match calculator */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        Single Elimination Match Calculator
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">The number of matches in a single elimination tournament is simply:</p>

                    <div className="my-6 inline-block rounded-2xl bg-[#EDEEF4] px-8 py-5 font-bebas text-3xl font-bold text-[#082F8A] tracking-wide">
                        n − 1
                    </div>
                    <p className="font-inter text-[#1a1a2e]/80 text-base mb-2">Where <strong>n</strong> is the number of players or teams.</p>

                    <div className="overflow-x-auto mt-6">
                        <div className="min-w-[560px] border border-[#d9dce7] rounded-2xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-[0.6fr_0.6fr_1.8fr] bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                <div className="p-4 border-r border-[#d9dce7]">Participants</div>
                                <div className="p-4 border-r border-[#d9dce7]">Matches</div>
                                <div className="p-4">Typical Rounds</div>
                            </div>
                            {CALC_TABLE.map((row, i) => (
                                <div key={row.n} className={`grid grid-cols-[0.6fr_0.6fr_1.8fr] border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-4 border-r border-[#d9dce7]">{row.n}</div>
                                    <div className="p-4 border-r border-[#d9dce7] font-medium">{row.matches}</div>
                                    <div className="p-4 text-[#1a1a2e]/80">{row.rounds}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use Single Elimination?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* SE vs RR */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Single Elimination vs Round Robin
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Single elimination is faster, while <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link> is fairer and gives every participant more matches.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Single Elimination</div>
                                <div className="p-5 text-[#1a1a2e]">Round Robin</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.se}</div>
                                    <div className="p-5 font-medium">{row.rr}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Knockout Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate brackets, manage registrations, track winners, publish live scores and run your tournament from one platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Single Elimination", "Double Elimination", "Round Robin", "Live Scores", "Player Rankings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Single Elimination FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
